export type BucketId =
  | "hooks"
  | "product-benefits"
  | "education"
  | "reviews"
  | "social-ugc"
  | "press-authority"
  | "transformation"
  | "comparison"
  | "native-memes"
  | "offers-promos"
  | "lifestyle-brand"
  | "other";

export type PresetId =
  | "high-converting"
  | "product-heavy"
  | "ugc-heavy"
  | "aggressive"
  | "balanced";

export interface BucketDef {
  id: BucketId;
  label: string;
  description: string;
}

export interface PresetDef {
  id: PresetId;
  label: string;
  slugs: string[];
}

export interface SlugMeta {
  shortName: string;
  bucket: BucketId;
}

// Ordered for the Ads Express picker — roughly top-of-funnel attention
// grabbers first, then proof/comparison mechanics, then offers/brand.
export const BUCKETS: BucketDef[] = [
  {
    id: "hooks",
    label: "Hooks & Headlines",
    description: "Bold hooks, curiosity, problem reveals",
  },
  {
    id: "product-benefits",
    label: "Product & Benefits",
    description: "Product hero, benefits, features, specs",
  },
  {
    id: "education",
    label: "Ingredient & Education",
    description: "Ingredient diagrams, callouts, explainers",
  },
  {
    id: "reviews",
    label: "Reviews & Testimonials",
    description: "Reviews, ratings, testimonials, quotes",
  },
  {
    id: "social-ugc",
    label: "Social & UGC",
    description: "UGC, tweets, DMs, comment screenshots",
  },
  {
    id: "press-authority",
    label: "Press & Authority",
    description: "Press logos, mastheads, expert proof",
  },
  {
    id: "transformation",
    label: "Before & After",
    description: "Before/after, transformations, routines",
  },
  {
    id: "comparison",
    label: "Comparison / Us vs Them",
    description: "Us vs them, competitor comparisons",
  },
  {
    id: "native-memes",
    label: "Native UI & Memes",
    description: "Memes, native UI fakes, advertorials",
  },
  {
    id: "offers-promos",
    label: "Offers & Promos",
    description: "Sales, BOGO, discounts, guarantees",
  },
  {
    id: "lifestyle-brand",
    label: "Lifestyle & Brand",
    description: "Lifestyle, flat lays, brand statements",
  },
  {
    id: "other",
    label: "Other",
    description: "Custom or brand-specific templates",
  },
];

export const SLUG_MAP: Record<string, SlugMeta> = {
  "bold-statement--reaction-headline": { shortName: "Bold Hook", bucket: "hooks" },
  "curiosity-hook-problem-revelation": { shortName: "Curiosity Hook", bucket: "hooks" },
  "problem-hook--benefit-stack": { shortName: "Problem Hook", bucket: "product-benefits" },
  "problem-illustration--product-solution": { shortName: "Problem Illustration", bucket: "hooks" },
  "social-comment--product": { shortName: "Demographic Hook", bucket: "social-ugc" },
  "category-reframe--benefit-callouts": { shortName: "Category Reframe", bucket: "education" },

  "product-hero--bullet-benefit-list": { shortName: "Bullet Breakdown", bucket: "product-benefits" },
  "product-hero--benefit-metric-stack": { shortName: "Benefit Metrics", bucket: "product-benefits" },
  "product-hero--claim-stack--cta": { shortName: "Claim Stack", bucket: "product-benefits" },
  "product-hero--streetwear-drop-hype": { shortName: "Streetwear Drop", bucket: "product-benefits" },
  "benefit-claim--product-callouts": { shortName: "Benefit Callouts", bucket: "education" },
  "benefit-stack--promo-offer": { shortName: "Promo Stack", bucket: "offers-promos" },
  "stat-surround--callout-radial-lifestyle-flatlay": { shortName: "Feature Infographic", bucket: "education" },
  "ingredient-transparency--benefit-stack": { shortName: "Ingredient Reveal", bucket: "education" },
  "long-form-manifesto--letter-ad": { shortName: "Price Justification", bucket: "hooks" },

  "17-verified-review-card": { shortName: "Verified Review", bucket: "reviews" },
  "highlighted-testimonial": { shortName: "Highlighted Quote", bucket: "reviews" },
  "pull-quote-review-card": { shortName: "Pull-Quote Card", bucket: "reviews" },
  "curiosity-gap--hook-quote-testimonial": { shortName: "Authority Quote", bucket: "reviews" },
  "product-hero--comment-screenshot": { shortName: "Comment Proof", bucket: "social-ugc" },
  "facebook-comment-thread": { shortName: "Comment Thread", bucket: "social-ugc" },
  "social_proof_stat": { shortName: "Credibility Stack", bucket: "reviews" },
  "press-authority-quote": { shortName: "Press Logos", bucket: "press-authority" },
  "press-feature-screenshot": { shortName: "Press Feature", bucket: "press-authority" },
  "ugc--viral-post-overlay": { shortName: "UGC Overlay", bucket: "social-ugc" },
  "ugc-lifestyle--product": { shortName: "UGC Lifestyle", bucket: "social-ugc" },
  "ugc-demonstration--visual-analogy": { shortName: "UGC Demo", bucket: "social-ugc" },
  "ugc-educational-overlay": { shortName: "UGC Education", bucket: "social-ugc" },
  "lifestyle-action--product": { shortName: "Lifestyle Action", bucket: "lifestyle-brand" },
  "before--after-ugc-native": { shortName: "Before/After UGC", bucket: "transformation" },

  "us_vs_them": { shortName: "Us vs Them", bucket: "comparison" },
  "us-vs-them-color-split": { shortName: "Color Split VS", bucket: "comparison" },
  "us-vs-them-grid-comparison": { shortName: "Grid Compare", bucket: "comparison" },
  "transformation-metric-testimonial": { shortName: "Transformation", bucket: "transformation" },
  "usage-demonstration--statistic-proof": { shortName: "Usage + Stats", bucket: "reviews" },
  "playful-graphic--product-hero": { shortName: "Playful Graphic", bucket: "product-benefits" },

  "action-figure-before--after": { shortName: "Archetype Meme", bucket: "transformation" },
  "negative-marketing-review": { shortName: "Negative Review", bucket: "native-memes" },
  "humorous-warning-product-ad": { shortName: "Humorous Warning", bucket: "native-memes" },
  "advertorial--editorial-content-card": { shortName: "Trend Disruption", bucket: "press-authority" },
  "x_post": { shortName: "X Post", bucket: "social-ugc" },
  "iphone-notes-app-screenshot": { shortName: "iPhone Note", bucket: "native-memes" },
};

export const PRESETS: PresetDef[] = [
  {
    id: "high-converting",
    label: "High Converting Mix",
    slugs: [
      "product-hero--bullet-benefit-list",
      "17-verified-review-card",
      "product-hero--comment-screenshot",
      "bold-statement--reaction-headline",
      "us_vs_them",
      "before--after-ugc-native",
      "curiosity-hook-problem-revelation",
      "benefit-stack--promo-offer",
    ],
  },
  {
    id: "product-heavy",
    label: "Product Heavy",
    slugs: [
      "product-hero--bullet-benefit-list",
      "product-hero--benefit-metric-stack",
      "product-hero--claim-stack--cta",
      "benefit-claim--product-callouts",
      "stat-surround--callout-radial-lifestyle-flatlay",
      "ingredient-transparency--benefit-stack",
    ],
  },
  {
    id: "ugc-heavy",
    label: "UGC / Social Proof Heavy",
    slugs: [
      "17-verified-review-card",
      "highlighted-testimonial",
      "pull-quote-review-card",
      "product-hero--comment-screenshot",
      "facebook-comment-thread",
      "ugc-lifestyle--product",
      "ugc--viral-post-overlay",
      "before--after-ugc-native",
    ],
  },
  {
    id: "aggressive",
    label: "Aggressive Hooks",
    slugs: [
      "bold-statement--reaction-headline",
      "curiosity-hook-problem-revelation",
      "negative-marketing-review",
      "humorous-warning-product-ad",
      "advertorial--editorial-content-card",
      "action-figure-before--after",
    ],
  },
  {
    id: "balanced",
    label: "Balanced Test Set",
    slugs: [
      "bold-statement--reaction-headline",
      "curiosity-hook-problem-revelation",
      "product-hero--bullet-benefit-list",
      "benefit-stack--promo-offer",
      "17-verified-review-card",
      "ugc-lifestyle--product",
      "us_vs_them",
      "transformation-metric-testimonial",
      "negative-marketing-review",
      "x_post",
    ],
  },
];

// Keyword heuristics for templates that aren't in SLUG_MAP yet (i.e.
// templates a user just added in Static Ad Units, or any unit without an
// explicit category). Order matters — the first bucket whose keyword
// matches wins, so the most specific mechanics come first. This keeps
// newly-created templates from silently falling into "Other".
const BUCKET_KEYWORDS: Array<{ bucket: BucketId; keywords: string[] }> = [
  {
    bucket: "transformation",
    keywords: [
      "before", "after", "before--after", "before/after", "old me",
      "new me", "old you", "new you", "old routine", "new routine",
      "transformation", "how it started", "then vs now",
      "from this to that", "day-labeled", "week-by-week",
    ],
  },
  {
    bucket: "comparison",
    keywords: [
      " vs ", "vs.", "-vs-", "versus", "us-vs-them", "us_vs_them",
      "generic vs", "competitor", "crossed-out", "alternatives",
      "comparison", "others vs us", "want vs try", "category default",
      "named competitor", "price per serving", "without vs with",
    ],
  },
  {
    bucket: "press-authority",
    keywords: [
      "press", "forbes", "vogue", "daily mail", "masthead", "as seen",
      "wirecutter", "advertorial", "editorial article", "article-style",
      "partner logo", "authority", "loved-by", "breaking news",
      "news article",
    ],
  },
  {
    bucket: "native-memes",
    keywords: [
      "airdrop", "incoming-call", "incoming call", "push-notification",
      "push notification", "notes app", "notes-app", "imessage",
      "ms paint", "drake", "meme", "fake google", "google search",
      "search result", "search autocomplete", "negative review",
      "negative marketing", "sarcastic", "hater comment", "humorous",
      "warning", "whiteboard", "sticky note", "reverse psychology",
      "don't buy", "symptom-quiz", "quiz ui", "modal style", "modal-style",
    ],
  },
  {
    bucket: "social-ugc",
    keywords: [
      "ugc", "tweet", "x post", "x_post", "x-post", "dm conversation",
      "facebook comment", "instagram", "insta sticker", "whatsapp",
      "friend text", "text-message", "text message", "social comment",
      "comment thread", "comment screenshot", "comment bubble",
      "comment collage", "comment over", "viral post", "selfie",
      "girl math", "comment",
    ],
  },
  {
    bucket: "reviews",
    keywords: [
      "review", "testimonial", "quote", "five star", "five-star",
      "5 star", "star-rated", "star rating", "trustpilot", "verified",
      "pull-quote", "pull quote", "credibility", "lives changed",
      "review count", "big-stat", "big number", "objection-handler",
      "objection handler",
    ],
  },
  {
    bucket: "offers-promos",
    keywords: [
      "bogo", "buy one", "buy this get", "sale", "discount", "% off",
      "percent-off", "percent off", "promo", "money-back", "money back",
      "guarantee", "free shipping", "restock", "back in stock",
      "sitewide", "black friday", "cyber monday", "holiday sale",
      "flash sale", "abandoned-cart", "urgency", "savings equation",
    ],
  },
  {
    bucket: "education",
    keywords: [
      "ingredient", "anatomy", "diagram", "did you know", "teardown",
      "infographic", "spoke", "annotated", "callout", "callouts",
      "stat stack", "clinical stats", "educational", "carousel cover",
      "symptom circle", "benefit map", "radial benefit", "no-bad-stuff",
    ],
  },
  {
    bucket: "product-benefits",
    keywords: [
      "product hero", "product-hero", "product_hero", "product photo",
      "product card", "benefit", "feature", "spec", "bullet",
      "numbered benefit", "numbered feature", "reasons to try",
      "reasons-to-try", "reasons with product", "variant", "packshot",
      "packaging", "bundle", "hero product", "bestseller", "claim",
      "metric", "manifesto", "letter-ad",
    ],
  },
  {
    bucket: "hooks",
    keywords: [
      "hook", "bold statement", "bold headline", "curiosity", "problem",
      "question", "reveal", "reaction", "scroll", "reframe",
      "mega-headline", "world's first", "stat headline", "empathy",
      "reassurance", "trend", "disruption", "saas hero",
      "value justification", "nostalgia",
    ],
  },
  {
    bucket: "lifestyle-brand",
    keywords: [
      "lifestyle", "flat lay", "flat-lay", "brand statement", "tagline",
      "billboard", "streetwear", "drop", "minimal", "serif",
      "color field", "environmental depth", "themed color",
      "scatter props", "jewelry", "luxury", "maker personal",
    ],
  },
];

function inferBucket(text: string): BucketId | null {
  const t = text.toLowerCase();
  for (const { bucket, keywords } of BUCKET_KEYWORDS) {
    if (keywords.some((kw) => t.includes(kw))) return bucket;
  }
  return null;
}

function isGenericProjectQuoteImportName(name: string | null | undefined): boolean {
  return typeof name === "string"
    && /^project\s+quote\s+template\s+\d+$/i.test(name.trim());
}

const VALID_BUCKETS = new Set<BucketId>(BUCKETS.map((b) => b.id));

function isBucketId(v: unknown): v is BucketId {
  return typeof v === "string" && VALID_BUCKETS.has(v as BucketId);
}

export function bucketOf(
  slug: string | null | undefined,
  name?: string | null,
  explicit?: string | null,
): BucketId {
  // Explicit category chosen by the user when creating the template wins.
  if (isBucketId(explicit)) return explicit;
  if (slug && SLUG_MAP[slug]) return SLUG_MAP[slug].bucket;
  // "Project Quote" is the importing brand name in these generic numbered
  // rows, not a review mechanic. Keep this exact generic-name exception ahead
  // of keyword inference so descriptive quote/review templates still match.
  if (isGenericProjectQuoteImportName(name)) return "other";
  const haystack = `${slug ?? ""} ${name ?? ""}`.trim();
  if (!haystack) return "other";
  return inferBucket(haystack) ?? "other";
}

// Convert a slug like "my-cool-template" or a name like "My Cool Template"
// into a short, title-cased label for the chip in Ads Express.
function titleCaseFromSlug(slug: string): string {
  return slug
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w.length === 0 ? w : w[0].toUpperCase() + w.slice(1)))
    .join(" ");
}

export function shortNameOf(slug: string | null | undefined, fallback: string): string {
  if (slug && SLUG_MAP[slug]) return SLUG_MAP[slug].shortName;
  if (fallback && fallback.trim().length > 0) return fallback;
  if (slug) return titleCaseFromSlug(slug);
  return fallback;
}
